import { useState } from 'react';
import { Send } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { leadApi } from '@/services/api';
import { trackReveal, ListingType } from '@/lib/tracking';
import {
  ZameenContactHeader,
  ZameenContactActions,
  ZameenField,
  zameenInputClass,
  zameenTextareaClass,
} from '@/components/property-detail/ZameenContactParts';

interface EmailContactDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  agentName?: string;
  agencyName?: string;
  agentLogo?: string | null;
  phoneNumber?: string;
  referenceId: string;
  propertyId?: string;
  projectId?: string;
  listingId?: string;
  listingType?: ListingType;
}

export function EmailContactDialog({
  open,
  onOpenChange,
  agentName = 'Property Agent',
  agencyName,
  agentLogo,
  phoneNumber,
  referenceId,
  propertyId,
  projectId,
  listingId,
  listingType,
}: EmailContactDialogProps) {
  const defaultMsg = `I would like to inquire about your property EthioRealEstates - ${referenceId}. Please contact me at your earliest convenience.`;
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: defaultMsg });
  const [role, setRole] = useState('buyer');
  const [keepInformed, setKeepInformed] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSubmitting(true);
    try {
      await leadApi.create({
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        message: form.message.trim() || undefined,
        property_id: propertyId || undefined,
        project_id: projectId || undefined,
        source: 'email_dialog',
      });
      const finalListingId = listingId || propertyId || projectId;
      const finalType: ListingType | undefined = listingType
        || (propertyId ? 'property' : projectId ? 'project' : undefined);
      if (finalListingId && finalType) {
        trackReveal({ listingType: finalType, listingId: finalListingId, revealType: 'email' });
      }
      toast.success('Your inquiry has been sent!');
      onOpenChange(false);
      setForm({ name: '', email: '', phone: '', message: defaultMsg });
    } catch {
      toast.error('Failed to send inquiry. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-md p-0 gap-0 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
      >
        <DialogHeader className="px-5 pt-5 pb-2">
          <DialogTitle className="sr-only">Contact Agent</DialogTitle>
        </DialogHeader>

        <div className="px-5 pb-5">
          <ZameenContactHeader
            logoUrl={agentLogo}
            agencyName={agencyName || agentName}
            agentName={agentName}
            refId={referenceId}
          />

          <ZameenContactActions phone={phoneNumber || '+251911000000'} />

          <form onSubmit={handleSubmit} className="space-y-3">
            <ZameenField label="Name" required htmlFor="ec-name">
              <input
                id="ec-name"
                required
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                className={zameenInputClass}
              />
            </ZameenField>

            <ZameenField label="Email" required htmlFor="ec-email">
              <input
                id="ec-email"
                type="email"
                required
                value={form.email}
                onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                className={zameenInputClass}
              />
            </ZameenField>

            <ZameenField label="Phone" required htmlFor="ec-phone">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground shrink-0">🇪🇹 +251</span>
                <input
                  id="ec-phone"
                  type="tel"
                  required
                  value={form.phone}
                  onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                  className={zameenInputClass}
                />
              </div>
            </ZameenField>

            <ZameenField label="Message" required htmlFor="ec-message">
              <textarea
                id="ec-message"
                rows={3}
                required
                value={form.message}
                onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
                className={zameenTextareaClass}
              />
            </ZameenField>

            <div className="space-y-2 pt-1">
              <Label className="text-xs font-medium">I am a:</Label>
              <RadioGroup value={role} onValueChange={setRole} className="flex gap-4 flex-wrap">
                {[
                  { value: 'buyer', label: 'Buyer / Tenant' },
                  { value: 'agent', label: 'Agent' },
                  { value: 'other', label: 'Other' },
                ].map((opt) => (
                  <div key={opt.value} className="flex items-center gap-1.5">
                    <RadioGroupItem value={opt.value} id={`ec-role-${opt.value}`} />
                    <Label htmlFor={`ec-role-${opt.value}`} className="text-xs cursor-pointer">{opt.label}</Label>
                  </div>
                ))}
              </RadioGroup>
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id="ec-keep-informed"
                checked={keepInformed}
                onCheckedChange={(c) => setKeepInformed(!!c)}
              />
              <Label htmlFor="ec-keep-informed" className="text-xs cursor-pointer">
                Keep me informed about similar properties.
              </Label>
            </div>

            <Button type="submit" className="w-full h-11 font-bold gap-2" disabled={submitting}>
              <Send className="h-4 w-4" />
              {submitting ? 'Sending...' : 'Send Message'}
            </Button>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
